/**
 * Frontend display overrides for bundle data from the catalog API.
 *
 * Keeps the backend catalog source-of-truth while allowing us to adjust
 * user-facing copy (e.g. product rebrands) and inject cross-product feature
 * rows (e.g. surfacing JotTransfer inclusion on tier cards) without a
 * round-trip through the backend or a Stripe product edit.
 */

import { useMemo } from 'react';
import { useFeatureGate } from '@services/statsig';
import { FeatureGates } from '@services/statsig/constants';

import type { BundleWithStatus, Feature } from './types';

const EHR_NOTE_COPY_CAPABILITY = 'ehr_note_copy';

const EHR_ADDON_DISPLAY = {
  name: 'JotTransfer',
  description:
    'Chrome extension for transferring notes directly into your EHR system',
};

/**
 * Rewrite a backend feature-row name to its JotTransfer equivalent.
 * Matches variations like "Unlimited EHR note copy", "EHR note copy", etc.
 */
function renameEhrAddonFeature(name: string): string {
  if (/ehr note cop(y|ies)/i.test(name)) {
    return /unlimited/i.test(name) ? 'Unlimited JotTransfers' : 'JotTransfers';
  }
  return name;
}

const JOTTRANSFER_TIER_FEATURE: Feature = {
  name: 'Unlimited JotTransfers',
  included: true,
};

function bundleGrantsEhrNoteCopy(bundle: BundleWithStatus): boolean {
  return bundle.grants?.some(g => g.capability_name === EHR_NOTE_COPY_CAPABILITY) ?? false;
}

function bundleGrantsUnlimitedEhrNoteCopy(bundle: BundleWithStatus): boolean {
  return (
    bundle.grants?.some(
      g => g.capability_name === EHR_NOTE_COPY_CAPABILITY && g.quota === null,
    ) ?? false
  );
}

interface DisplayOptions {
  /** Whether the JotTransfer (EHR extension) product is rolled out to this user. */
  ehrExtensionEnabled: boolean;
}

/**
 * Return a display-adjusted copy of a bundle.
 *
 * - Rebrands the EHR Extension add-on to "JotTransfer" in the name, description,
 *   and feature list.
 * - Appends "Unlimited JotTransfers" to tier bundles that grant unlimited
 *   ehr_note_copy usage, but only when the JotTransfer feature gate is on.
 */
export function getDisplayBundle(
  bundle: BundleWithStatus,
  opts: DisplayOptions,
): BundleWithStatus {
  let result = bundle;

  const isEhrAddon =
    bundle.bundle_type === 'add_on' && bundleGrantsEhrNoteCopy(bundle);

  if (isEhrAddon) {
    const renamedFeatures = (bundle.marketing_features ?? []).map(f => ({
      ...f,
      name: renameEhrAddonFeature(f.name),
    }));
    result = {
      ...bundle,
      name: EHR_ADDON_DISPLAY.name,
      description: EHR_ADDON_DISPLAY.description,
      marketing_features: renamedFeatures,
    };
  }

  const shouldInjectJotTransferOnTier =
    opts.ehrExtensionEnabled &&
    bundle.bundle_type === 'tier' &&
    bundleGrantsUnlimitedEhrNoteCopy(bundle);

  if (shouldInjectJotTransferOnTier) {
    const existing = result.marketing_features ?? [];
    const alreadyPresent = existing.some(f => /jottransfer/i.test(f.name));
    if (!alreadyPresent) {
      result = {
        ...result,
        marketing_features: [...existing, JOTTRANSFER_TIER_FEATURE],
      };
    }
  }

  return result;
}

/**
 * Hook wrapper for display-adjusting a single bundle. Resolves the JotTransfer
 * feature gate once and memoizes the result.
 */
export function useDisplayBundle(
  bundle: BundleWithStatus | null | undefined,
): BundleWithStatus | null {
  const ehrExtensionEnabled = useFeatureGate(FeatureGates.EHR_EXTENSION_ADDON);
  return useMemo(
    () => (bundle ? getDisplayBundle(bundle, { ehrExtensionEnabled }) : null),
    [bundle, ehrExtensionEnabled],
  );
}

/**
 * Hook wrapper for display-adjusting a list of bundles.
 */
export function useDisplayBundles(bundles: BundleWithStatus[]): BundleWithStatus[] {
  const ehrExtensionEnabled = useFeatureGate(FeatureGates.EHR_EXTENSION_ADDON);
  return useMemo(
    () => bundles.map(b => getDisplayBundle(b, { ehrExtensionEnabled })),
    [bundles, ehrExtensionEnabled],
  );
}
